import React from "react";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PeopleIcon from "@mui/icons-material/People";
import {
  Alert,
  Avatar,
  Chip,
  Divider,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { EVOTRUX_BLUE } from "constants/index";
import { emailIsValid } from "utils";

import type {
  NetworkGroup,
  NetworkMember,
  PartnerSelectionItem,
} from "../../types";

// Constants
const PARTNER_COLORS = {
  PRIMARY: EVOTRUX_BLUE,
  PRIMARY_LIGHT: "#e3f2fd",
  NETWORK_INVITE: "#616161",
  NETWORK_INVITE_LIGHT: "#f5f5f5",
} as const;

// Font sizes
const FONT_SIZES = {
  SMALL: "14px",
  ICON: 16,
  INHERIT: "inherit",
} as const;

// Spacing values
const SPACING = {
  SMALL_GAP: 8,
  MEDIUM_GAP: 12,
  AVATAR_SIZE: 32,
  ICON_MARGIN: 0.5,
  LIST_ITEM_PADDING: "8px 16px",
} as const;

// Typography variants
export const TYPOGRAPHY_VARIANTS = {
  BODY2: "body2" as const,
  CAPTION: "caption" as const,
} as const;

// Chip variants
export const CHIP_VARIANTS = {
  OUTLINED: "outlined" as const,
} as const;

// Theme colors
export const THEME_COLORS = {
  TEXT_SECONDARY: "text.secondary",
} as const;

// Email validation patterns
const EMAIL_SEPARATORS_REGEX = /[,;]/;
const MULTIPLE_SPACES_REGEX = /\s{2,}/;
const PASTE_SEPARATORS_REGEX = /[,;\s\n\r]/;

const AVATAR_STYLES = {
  width: SPACING.AVATAR_SIZE,
  height: SPACING.AVATAR_SIZE,
  fontSize: FONT_SIZES.SMALL,
  bgcolor: PARTNER_COLORS.PRIMARY_LIGHT,
  color: PARTNER_COLORS.PRIMARY,
} as const;

const LIST_ITEM_STYLES = {
  display: "flex",
  alignItems: "center",
  gap: SPACING.MEDIUM_GAP,
  padding: SPACING.LIST_ITEM_PADDING,
} as const;

// Utility functions
function generateUserInitials(name?: string, email?: string): string {
  if (name) {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  }
  return email?.[0]?.toUpperCase() ?? "";
}

function getFullName(firstName?: string, lastName?: string): string {
  return `${firstName ?? ""} ${lastName ?? ""}`.trim();
}

export function isOptionEqualToValue(
  option: string | PartnerSelectionItem,
  value: string | PartnerSelectionItem,
): boolean {
  // Handle string (freeSolo) comparisons
  if (typeof option === "string" && typeof value === "string") {
    return option === value;
  }

  // Handle object comparisons by type and unique identifier
  if (typeof option === "object" && typeof value === "object") {
    if (option.type !== value.type) return false;

    switch (option.type) {
      case "all_partners":
        return true; // Only one "all_partners" option exists
      case "partner":
        return (
          (option as { email: string }).email.toLowerCase() ===
          (value as { email: string }).email.toLowerCase()
        );
      case "group":
        return (option as { id: string }).id === (value as { id: string }).id;
      case "email":
        return (
          (option as { email: string }).email.toLowerCase() ===
          (value as { email: string }).email.toLowerCase()
        );
      default:
        return false;
    }
  }

  return false;
}

export function getOptionLabel(option: string | PartnerSelectionItem): string {
  if (typeof option === "string") {
    return option;
  }

  switch (option.type) {
    case "all_partners":
      return option.label;
    case "partner":
      return option.name
        ? `${option.name} (${option.email})`
        : `(${option.email})`;
    case "group":
      return `${option.name} • ${option.memberCount} carriers`;
    case "email":
      return option.email;
    default:
      return "";
  }
}

function getChipKey(option: string | PartnerSelectionItem): string {
  if (typeof option === "string") return option;
  if (option.type === "all_partners") return "all_partners";
  if (option.type === "partner") return option.email;
  if (option.type === "group") return option.id;
  return option.email;
}

function getChipStyles(option: string | PartnerSelectionItem) {
  const isNetworkInvite = typeof option === "string" || option.type === "email";

  return isNetworkInvite
    ? {
        bgcolor: PARTNER_COLORS.NETWORK_INVITE_LIGHT,
        color: PARTNER_COLORS.NETWORK_INVITE,
        borderColor: PARTNER_COLORS.NETWORK_INVITE,
      }
    : {
        bgcolor: PARTNER_COLORS.PRIMARY_LIGHT,
        color: PARTNER_COLORS.PRIMARY,
        borderColor: PARTNER_COLORS.PRIMARY,
      };
}

// Component render functions
export function renderStringOption(
  optionProps: any,
  option: string,
): React.ReactElement | null {
  if (!option || typeof option !== "string") {
    return null;
  }

  return (
    <li
      {...optionProps}
      style={{ display: "flex", alignItems: "center", gap: SPACING.SMALL_GAP }}
    >
      <Typography variant="body2" sx={{ fontSize: FONT_SIZES.SMALL }}>
        {option}
      </Typography>
    </li>
  );
}

export function renderAllPartnersOption(
  optionProps: any,
  option: PartnerSelectionItem & { type: "all_partners" },
): React.ReactElement {
  return (
    <>
      <li
        {...optionProps}
        style={{
          display: "flex",
          alignItems: "center",
          gap: SPACING.SMALL_GAP,
        }}
      >
        <PeopleIcon
          sx={{ fontSize: FONT_SIZES.ICON, color: PARTNER_COLORS.PRIMARY }}
        />
        {option.label}
      </li>
      <Divider />
    </>
  );
}

export function renderPartnerOption(
  optionProps: any,
  option: PartnerSelectionItem & { type: "partner" },
  confirmedPartners: NetworkMember[],
): React.ReactElement {
  const initials = generateUserInitials(option.name, option.email);
  const partnerInfo = confirmedPartners.find(
    (p) => p.email.toLowerCase() === option.email.toLowerCase(),
  );

  return (
    <li {...optionProps} style={LIST_ITEM_STYLES}>
      <Avatar sx={AVATAR_STYLES}>{initials}</Avatar>
      <Stack>
        <Typography variant={TYPOGRAPHY_VARIANTS.BODY2}>
          {option.name ? `${option.name} (${option.email})` : option.email}
        </Typography>
        <Typography
          variant={TYPOGRAPHY_VARIANTS.CAPTION}
          color={THEME_COLORS.TEXT_SECONDARY}
        >
          {partnerInfo?.companyName}
        </Typography>
      </Stack>
    </li>
  );
}

export function renderGroupOption(
  optionProps: any,
  option: PartnerSelectionItem & { type: "group" },
): React.ReactElement {
  return (
    <li {...optionProps} style={LIST_ITEM_STYLES}>
      <PeopleIcon
        sx={{ fontSize: FONT_SIZES.ICON, color: PARTNER_COLORS.PRIMARY }}
      />
      <Stack>
        <Typography variant={TYPOGRAPHY_VARIANTS.BODY2}>
          {option.name}
        </Typography>
        <Typography
          variant={TYPOGRAPHY_VARIANTS.CAPTION}
          color={THEME_COLORS.TEXT_SECONDARY}
        >
          Group • {option.memberCount} carriers
        </Typography>
      </Stack>
    </li>
  );
}

export function renderEmailOption(
  optionProps: any,
  option: PartnerSelectionItem & { type: "email" },
): React.ReactElement {
  return (
    <li
      {...optionProps}
      style={{ display: "flex", alignItems: "center", gap: SPACING.SMALL_GAP }}
    >
      <PeopleIcon
        sx={{ fontSize: FONT_SIZES.ICON, color: PARTNER_COLORS.NETWORK_INVITE }}
      />
      <Typography sx={{ color: PARTNER_COLORS.NETWORK_INVITE }}>
        {option.email}
      </Typography>
    </li>
  );
}

export function renderChip(
  option: string | PartnerSelectionItem,
  index: number,
  getTagProps: (props: { index: number }) => any,
): React.ReactElement {
  const chipStyles = getChipStyles(option);
  const key = getChipKey(option);
  const label = getOptionLabel(option);

  return (
    <Chip
      variant={CHIP_VARIANTS.OUTLINED}
      label={label}
      {...getTagProps({ index })}
      sx={chipStyles}
      key={key}
    />
  );
}

interface PrivateSelectionErrorMessageProps {
  hasConfirmedPartners: boolean;
  hasPendingInvites: boolean;
}

export function buildPrivateSelectionErrorMessage(
  props: PrivateSelectionErrorMessageProps,
): string {
  const { hasConfirmedPartners, hasPendingInvites } = props;

  if (hasConfirmedPartners) {
    return 'Please select at least one partner, add an email address, or choose "All my partners"';
  }
  if (hasPendingInvites) {
    return "Please select a pending invite or add a carrier email address";
  }
  return "Please add a carrier email address to send this private shipment to";
}

export function renderPendingInvitesOnlyAlert(
  pendingInviteCount: number,
): React.ReactElement {
  return (
    <Alert severity="info">
      No carrier has joined your network yet, but{" "}
      {pendingInviteCount === 1
        ? "1 invite is"
        : `${pendingInviteCount} invites are`}{" "}
      still awaiting a response. Select one above to send this shipment their
      way once they accept, or add more emails.
    </Alert>
  );
}

export function renderNoPartnersAlert(): React.ReactElement {
  return (
    <Alert severity="info">
      You have no partners in your network yet. You can add partners by typing
      their email above or{" "}
      <Tooltip title="Network Page">
        <Link
          href="/network"
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          aria-label="head to network page (network page)"
          sx={{ display: "inline-flex", alignItems: "center" }}
        >
          head to the network page!
          <OpenInNewIcon
            sx={{ ml: SPACING.ICON_MARGIN, fontSize: FONT_SIZES.INHERIT }}
          />
        </Link>
      </Tooltip>
    </Alert>
  );
}

// Selection processing utilities
function emailIsInList(email: string, emails: string[]): boolean {
  const target = email.toLowerCase();
  return emails.some((candidate) => candidate.toLowerCase() === target);
}

function getConfirmedPartnerEmails(
  confirmedPartners: NetworkMember[],
): string[] {
  return confirmedPartners.map((partner) => partner.email);
}

function processStringToPartnerItem(
  item: string,
  confirmedPartners: NetworkMember[],
): PartnerSelectionItem | null {
  if (!emailIsValid(item)) {
    return null;
  }

  const email = item.toLowerCase();
  const isConfirmedPartner = confirmedPartners.some(
    (p) => p.email.toLowerCase() === email,
  );

  if (isConfirmedPartner) {
    const partner = confirmedPartners.find(
      (p) => p.email.toLowerCase() === email,
    );
    return {
      type: "partner",
      email,
      name: partner
        ? getFullName(partner.firstName, partner.lastName)
        : undefined,
    };
  }

  return { type: "email", email };
}

function processSelections(
  newSelections: (string | PartnerSelectionItem)[],
  confirmedPartners: NetworkMember[],
): PartnerSelectionItem[] {
  return newSelections
    .map((item) => {
      if (typeof item === "string") {
        return processStringToPartnerItem(item, confirmedPartners);
      }
      return item;
    })
    .filter((item): item is PartnerSelectionItem => item !== null);
}

function categorizeSelections(processedSelections: PartnerSelectionItem[]) {
  const partnerEmails = processedSelections
    .filter(
      (item): item is PartnerSelectionItem & { type: "partner" } =>
        item.type === "partner",
    )
    .map((item) => item.email);

  const groupIds = processedSelections
    .filter(
      (item): item is PartnerSelectionItem & { type: "group" } =>
        item.type === "group",
    )
    .map((item) => item.id);

  // Posted as network_invite_emails, the only field the backend can accept for
  // an address with no carrier account behind it yet.
  const networkInviteEmails = processedSelections
    .filter(
      (item): item is PartnerSelectionItem & { type: "email" } =>
        item.type === "email",
    )
    .map((item) => item.email);

  return { partnerEmails, groupIds, networkInviteEmails };
}

// Options creation utility
interface CreateOptionsProps {
  confirmedPartners: NetworkMember[];
  availableGroups: NetworkGroup[];
  pendingInviteEmails: string[];
  allowGroups: boolean;
}

export function createOptions(
  props: CreateOptionsProps,
): PartnerSelectionItem[] {
  const {
    confirmedPartners,
    availableGroups,
    pendingInviteEmails,
    allowGroups,
  } = props;
  const options: PartnerSelectionItem[] = [];

  // Only show "All my partners" option if there are confirmed partners
  if (confirmedPartners.length > 0) {
    options.push({ type: "all_partners", label: "All my partners" });
  }

  if (allowGroups) {
    options.push(
      ...availableGroups.map((group) => ({
        type: "group" as const,
        id: group.group.id.toString(),
        name: group.group.name,
        memberCount: group.members.length,
      })),
    );
  }

  options.push(
    ...confirmedPartners.map((partner) => ({
      type: "partner" as const,
      email: partner.email,
      name: getFullName(partner.firstName, partner.lastName),
    })),
  );

  // Listed last so an established network is not diluted by outstanding
  // invites. An address that is somehow both is offered once, as the partner.
  if (pendingInviteEmails.length > 0) {
    const confirmedPartnerEmails = getConfirmedPartnerEmails(confirmedPartners);
    options.push(
      ...pendingInviteEmails
        .filter((email) => !emailIsInList(email, confirmedPartnerEmails))
        .map((email) => ({ type: "email" as const, email })),
    );
  }

  return options;
}

interface ClassifyPrefilledPartnerEmailsProps {
  selectedConfirmedPartnerEmails: string[];
  selectedNetworkInviteEmails: string[];
  confirmedPartners: NetworkMember[];
}

interface PrefilledPartnerEmailClassification {
  confirmedPartnerEmails: string[];
  networkInviteEmails: string[];
}

/**
 * Re-buckets prefilled partner addresses that no longer match a confirmed partner.
 *
 * A shipment template or a copied shipment carries the addresses picked when it was
 * made, and one of them may since have lost its account. Posted as a confirmed
 * partner such an address has nothing to grant access to; posted as an invite it
 * reaches the person. Callers must wait for the partner list to load before using
 * this, since mid-load every address looks unmatched.
 */
export function classifyPrefilledPartnerEmails(
  props: ClassifyPrefilledPartnerEmailsProps,
): PrefilledPartnerEmailClassification {
  const {
    selectedConfirmedPartnerEmails,
    selectedNetworkInviteEmails,
    confirmedPartners,
  } = props;

  const partnerEmails = getConfirmedPartnerEmails(confirmedPartners);
  const isConfirmedPartner = (email: string) =>
    emailIsInList(email, partnerEmails);

  return {
    confirmedPartnerEmails:
      selectedConfirmedPartnerEmails.filter(isConfirmedPartner),
    networkInviteEmails: [
      ...selectedNetworkInviteEmails,
      ...selectedConfirmedPartnerEmails.filter(
        (email) =>
          !isConfirmedPartner(email) &&
          !emailIsInList(email, selectedNetworkInviteEmails),
      ),
    ],
  };
}

// Selected items creation utility
interface CreateSelectedItemsProps {
  isAllPartnersSelected: boolean;
  selectedConfirmedPartnerEmails: string[];
  selectedGroups: string[];
  selectedNetworkInviteEmails: string[];
  confirmedPartners: NetworkMember[];
  availableGroups: NetworkGroup[];
  allowGroups: boolean;
}

export function createSelectedItems(
  props: CreateSelectedItemsProps,
): PartnerSelectionItem[] {
  const {
    isAllPartnersSelected,
    selectedConfirmedPartnerEmails,
    selectedGroups,
    selectedNetworkInviteEmails,
    confirmedPartners,
    availableGroups,
    allowGroups,
  } = props;
  const selectedItems: PartnerSelectionItem[] = [];

  if (isAllPartnersSelected && confirmedPartners.length > 0) {
    selectedItems.push({ type: "all_partners", label: "All my partners" });
  } else {
    // Add confirmed partners
    selectedConfirmedPartnerEmails.forEach((email) => {
      const partner = confirmedPartners.find(
        (p) => p.email.toLowerCase() === email.toLowerCase(),
      );
      selectedItems.push({
        type: "partner",
        email,
        name: partner
          ? getFullName(partner.firstName, partner.lastName)
          : undefined,
      });
    });

    // Add groups (only for private shipments)
    if (allowGroups) {
      selectedGroups.forEach((groupId) => {
        const group = availableGroups.find(
          (g) => g.group.id.toString() === groupId,
        );
        if (group) {
          selectedItems.push({
            type: "group",
            id: groupId,
            name: group.group.name,
            memberCount: group.members.length,
          });
        }
      });
    }
  }

  // Always add network invite emails (regardless of "All my partners" selection)
  selectedNetworkInviteEmails.forEach((email) => {
    selectedItems.push({ type: "email", email });
  });

  return selectedItems;
}

// Selection change handlers
interface SelectionChangeHandlers {
  onConfirmedPartnerEmailsChange: (emails: string[]) => void;
  onSelectedGroupsChange: (groups: string[]) => void;
  onNetworkInviteEmailsChange: (emails: string[]) => void;
  onAllPartnersSelectedChange: (isSelected: boolean) => void;
}

interface SelectionChangeProps extends SelectionChangeHandlers {
  selectedItems: PartnerSelectionItem[];
  confirmedPartners: NetworkMember[];
  allowGroups: boolean;
}

// Helper functions for selection change
function clearIndividualSelections(
  onConfirmedPartnerEmailsChange: (emails: string[]) => void,
  onSelectedGroupsChange: (groups: string[]) => void,
  onAllPartnersSelectedChange: (isSelected: boolean) => void,
  allowGroups: boolean,
): void {
  onAllPartnersSelectedChange(true);
  onConfirmedPartnerEmailsChange([]);
  if (allowGroups) {
    onSelectedGroupsChange([]);
  }
  // Network invite emails are intentionally kept - they don't conflict with
  // "All my partners".
}

function applyIndividualSelections(
  processedSelections: PartnerSelectionItem[],
  onConfirmedPartnerEmailsChange: (emails: string[]) => void,
  onSelectedGroupsChange: (groups: string[]) => void,
  onNetworkInviteEmailsChange: (emails: string[]) => void,
  onAllPartnersSelectedChange: (isSelected: boolean) => void,
  allowGroups: boolean,
): void {
  onAllPartnersSelectedChange(false);

  const { partnerEmails, groupIds, networkInviteEmails } =
    categorizeSelections(processedSelections);

  onConfirmedPartnerEmailsChange(partnerEmails);
  if (allowGroups) {
    onSelectedGroupsChange(groupIds);
  }
  onNetworkInviteEmailsChange(networkInviteEmails);
}

export function handleSelectionChange(
  newSelections: (string | PartnerSelectionItem)[],
  props: SelectionChangeProps,
): void {
  const {
    selectedItems,
    confirmedPartners,
    allowGroups,
    onConfirmedPartnerEmailsChange,
    onSelectedGroupsChange,
    onNetworkInviteEmailsChange,
    onAllPartnersSelectedChange,
  } = props;

  // Check what was added to determine priority
  const previousHadAllPartners = selectedItems.some(
    (item) => item.type === "all_partners",
  );
  const newHasAllPartners = newSelections.some(
    (item) => typeof item === "object" && item.type === "all_partners",
  );
  const allPartnersWasAdded = newHasAllPartners && !previousHadAllPartners;

  const processedSelections = processSelections(
    newSelections,
    confirmedPartners,
  );

  const hasAllPartners = processedSelections.some(
    (item) => item.type === "all_partners",
  );
  const hasIndividualSelections = processedSelections.some(
    (item) => item.type === "partner" || item.type === "group",
  );

  // If both "All partners" and individual selections exist, determine priority
  if (hasAllPartners && hasIndividualSelections) {
    if (allPartnersWasAdded) {
      // "All my partners" was just added - clear individual selections
      clearIndividualSelections(
        onConfirmedPartnerEmailsChange,
        onSelectedGroupsChange,
        onAllPartnersSelectedChange,
        allowGroups,
      );
    } else {
      // Individual selections take priority - clear "All my partners"
      const filteredSelections = processedSelections.filter(
        (item) => item.type !== "all_partners",
      );
      applyIndividualSelections(
        filteredSelections,
        onConfirmedPartnerEmailsChange,
        onSelectedGroupsChange,
        onNetworkInviteEmailsChange,
        onAllPartnersSelectedChange,
        allowGroups,
      );
    }
  } else if (hasAllPartners) {
    // "All my partners" is selected with only network invite emails (no conflicts)
    // Extract and preserve the network invite emails
    const { networkInviteEmails } = categorizeSelections(processedSelections);

    onAllPartnersSelectedChange(true);
    onConfirmedPartnerEmailsChange([]);
    if (allowGroups) {
      onSelectedGroupsChange([]);
    }
    onNetworkInviteEmailsChange(networkInviteEmails);
  } else {
    // Process specific selections
    applyIndividualSelections(
      processedSelections,
      onConfirmedPartnerEmailsChange,
      onSelectedGroupsChange,
      onNetworkInviteEmailsChange,
      onAllPartnersSelectedChange,
      allowGroups,
    );
  }
}

// Key down handler
interface KeyDownHandlerProps {
  inputValue: string;
  confirmedPartners: NetworkMember[];
  selectedConfirmedPartnerEmails: string[];
  selectedNetworkInviteEmails: string[];
  onConfirmedPartnerEmailsChange: (emails: string[]) => void;
  onNetworkInviteEmailsChange: (emails: string[]) => void;
  onInputValueChange: (value: string) => void;
  onErrorChange?: (error: string | null) => void;
}

function processEmailInput(props: KeyDownHandlerProps): void {
  const {
    inputValue,
    confirmedPartners,
    selectedConfirmedPartnerEmails,
    selectedNetworkInviteEmails,
    onConfirmedPartnerEmailsChange,
    onNetworkInviteEmailsChange,
    onInputValueChange,
    onErrorChange,
  } = props;

  if (!inputValue.trim()) return;

  // Always split input by common separators to handle multiple emails
  const emails = inputValue
    .split(/[,;\s\n\r]+/)
    .map((email) => email.trim())
    .filter((email) => email.length > 0);

  const validEmails: string[] = [];
  const invalidEmails: string[] = [];

  // Process each email
  emails.forEach((email) => {
    if (emailIsValid(email)) {
      const emailLower = email.toLowerCase();

      // Check if already selected
      const alreadySelected =
        emailIsInList(emailLower, selectedConfirmedPartnerEmails) ||
        emailIsInList(emailLower, selectedNetworkInviteEmails);

      if (!alreadySelected) {
        validEmails.push(emailLower);
      }
    } else {
      invalidEmails.push(email);
    }
  });

  // Add valid emails to appropriate lists
  if (validEmails.length > 0) {
    const confirmedEmails: string[] = [];
    const inviteEmails: string[] = [];

    validEmails.forEach((email) => {
      const isConfirmedPartner = confirmedPartners.some(
        (p) => p.email.toLowerCase() === email,
      );

      if (isConfirmedPartner) {
        confirmedEmails.push(email);
      } else {
        inviteEmails.push(email);
      }
    });

    // Update confirmed partners
    if (confirmedEmails.length > 0) {
      onConfirmedPartnerEmailsChange([
        ...selectedConfirmedPartnerEmails,
        ...confirmedEmails,
      ]);
    }

    // Update network invite emails
    if (inviteEmails.length > 0) {
      onNetworkInviteEmailsChange([
        ...selectedNetworkInviteEmails,
        ...inviteEmails,
      ]);
    }
  }

  // If there are invalid emails, keep them in the input field for user to fix
  if (invalidEmails.length > 0) {
    onInputValueChange(invalidEmails.join(", "));
    onErrorChange?.(
      `Invalid email${invalidEmails.length > 1 ? "s" : ""}: ${invalidEmails.join(", ")}`,
    );
  } else {
    // Clear the input field only if all emails were valid
    onInputValueChange("");
    onErrorChange?.(null);
  }
}

function validateAndProcessInput(props: KeyDownHandlerProps): void {
  const { inputValue, onErrorChange } = props;

  if (!inputValue.trim()) return;

  const trimmedValue = inputValue.trim();

  // Check if input contains separators (comma, semicolon, or multiple spaces)
  if (
    EMAIL_SEPARATORS_REGEX.test(trimmedValue) ||
    MULTIPLE_SPACES_REGEX.test(trimmedValue)
  ) {
    // Multiple emails - check if they're all invalid (don't re-process if they are)
    const emails = trimmedValue
      .split(/[,;\s\n\r]+/)
      .map((email) => email.trim())
      .filter((email) => email.length > 0);
    const allInvalid = emails.every((email) => !emailIsValid(email));

    if (allInvalid) {
      onErrorChange?.(
        `Invalid email${emails.length > 1 ? "s" : ""}: ${emails.join(", ")}`,
      );
      return;
    }

    processEmailInput(props);
  } else {
    // Single email validation
    const isValid = emailIsValid(trimmedValue);
    if (isValid) {
      processEmailInput(props);
    } else {
      onErrorChange?.(`Invalid email: ${trimmedValue}`);
    }
  }
}

export function handleKeyDown(
  event: React.KeyboardEvent,
  props: KeyDownHandlerProps,
): void {
  if (event.key === "Enter" || event.key === " " || event.key === ",") {
    validateAndProcessInput(props);
    event.preventDefault();
  }
}

export function handleBlur(props: KeyDownHandlerProps): void {
  validateAndProcessInput(props);
}

export function handlePaste(
  event: React.ClipboardEvent,
  props: KeyDownHandlerProps,
): void {
  const { clipboardData } = event;
  if (!clipboardData) {
    return;
  }

  const pastedText = clipboardData.getData("text");
  if (!pastedText.trim()) return;

  const trimmedPaste = pastedText.trim();

  // Check if paste contains multiple emails (has separators)
  const hasSeparators = PASTE_SEPARATORS_REGEX.test(trimmedPaste);

  if (hasSeparators) {
    // Prevent default paste behavior
    event.preventDefault();

    // Process the pasted text directly
    const modifiedProps = {
      ...props,
      inputValue: pastedText,
    };

    processEmailInput(modifiedProps);
  }
  // If single email (valid or invalid), let default paste behavior work
}
